import {Button, Modal} from 'antd'
import {
    course_createCourseClass_Api,
    course_deleteCourseClass_Api,
    course_editCourseClass_Api,
    course_getCourseClasses_Api,
    course_getCourseClassesByOwner_Api,
} from 'apis'
import {getId} from 'apis/model/base'
import {LayoutContext} from 'components/layouts/Default/LayoutContext'
import {FullSideModalLayout} from 'components/Modal/SideModalLayout'
import _ from 'lodash'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useMemo, useState} from 'react'
import {RiListCheck2, RiSubscript, RiUserSharedLine} from 'react-icons/ri'
import {useLocation} from 'react-use'
import {branch, compose, nest, withProps} from 'recompose'
import {ContentNotFound} from 'views/AppLocals/ContentNotFound'
import {ClassActionTypes, CourseActionTypes} from 'views/AppLocals/Course'
import {FetchClassById} from 'views/AppLocals/Course/components/FetchEntity'
import {CreateCourseClassModal, EditCourseClassModal,} from 'views/AppLocals/Course/CourseClass/CourseClassForm'
import {CourseClassStudentList} from 'views/AppLocals/Course/CourseClass/CourseClassStudentForm'
import {CourseClassTimetables} from 'views/AppLocals/Course/CourseClass/CourseClassTimetableForm'
import {AppEntities} from 'views/AppLocals/enums'
import {showDeleteConfirmDialog} from 'views/AppLocals/factory/createConfirmDialog'
import createContextMenu from 'views/AppLocals/factory/createContextMenu'
import {notifyOnError} from 'views/AppLocals/factory/createErrorEvent'
import {NotificationActionTypes, successNotify,} from 'views/AppLocals/factory/createNotification'
import {bindQueryParam, bindQueryParams,} from 'views/AppLocals/functions/routerHelper'
import usePaginationHelper, {createPagerKey,} from 'views/AppLocals/hooks/usePaginationHelper'
import ListHeader from 'views/AppLocals/Templates/items/ListHeader'
import {NullAsDefault, renderTrue} from 'views/Shared'
import {bindingSearch} from 'views/Shared/components/PagerControls'
import CourseClassFilter from '../components/filters/CourseClassFilter'
import {renderCourseClass} from '../functions/renderCourseClass'
import {BiSolidEdit, BiTrash} from "react-icons/bi";
import {useRouteSearchParam} from "../../../../envs/_current/history";
import {CourseClassSubjectList} from "../../../AppLocals/Course/CourseClass/components/CourseClassSubjectForm";
import {CourseClassProvider} from "../../../AppLocals/Course/CourseClassContext";
import LoadingBlock from "../../../Loading/LoadingBlock";

const ClassesManager = ({
                            owner_id,
                            owner_type,
                            filter_option = renderTrue,
                            filter_actions = {
                                sortBy: 'sort-by',
                                keyword: 'keyword',
                                course_id: 'course-id',
                            },
                            ...props
                        }) => {
    const t = useTranslate()

    const location = useLocation()

    const {isSm} = useContext(LayoutContext)

    const sortBy = useRouteSearchParam(filter_actions.sortBy)

    const course_id = useRouteSearchParam(filter_actions.course_id)

    const [action, setAction] = useState({
        type: undefined,
        value: undefined,
    })

    const [refreshKey, setRefreshKey] = useState()

    const onError = notifyOnError(t)

    const reload = () => {
        setRefreshKey(Date.now())
        onCancel()
    }

    const {handleAsyncAction: onCreate} = useAsyncAction({
        apiInfo: course_createCourseClass_Api,
        onError,
        onSuccess: () => {
            successNotify(NotificationActionTypes.CREATE, t)
            reload()
        },
    })

    const {handleAsyncAction: onUpdate} = useAsyncAction({
        apiInfo: course_editCourseClass_Api,
        onError,
        onSuccess: () => {
            successNotify(NotificationActionTypes.UPDATE, t)
            reload()
        },
    })

    const {handleAsyncAction: onDelete} = useAsyncAction({
        apiInfo: course_deleteCourseClass_Api,
        onError,
        onSuccess: () => {
            successNotify(NotificationActionTypes.DELETE, t)
            reload()
        },
    })

    const confirmDelete = useCallback(
        (id) => {
            if (id) {
                showDeleteConfirmDialog({
                    translate: t,
                    title: 'delete class of course',
                    message: 'are you sure you want to delete this class of course',
                    onOk: () => {
                        onDelete({}, {id})
                    },
                })
            }
        },
        [onDelete]
    )

    const handleAction = (action, params) => {
        switch (action) {
            case ClassActionTypes.SHOW_TEACHER_LIST_MODAL:
            case ClassActionTypes.SHOW_SUBJECT_LIST_MODAL:
            case ClassActionTypes.SHOW_STUDENT_LIST_MODAL:
            case ClassActionTypes.SHOW_TIMETABLE_LIST_MODAL:
            case ClassActionTypes.CLASS_UPDATE:
            case ClassActionTypes.CLASS_CREATE:
                setAction({type: action, value: params})
                break
            case ClassActionTypes.CLASS_DELETE:
                confirmDelete(getId(params))
                break
            default:
                break
        }
    }

    const onCancel = () => {
        setAction({})
    }

    const renderContextMenu = useCallback(
        (item) =>
            createContextMenu(
                [
                    {
                        icon: <RiListCheck2 size={14} className="text-blue-500"/>,
                        label: 'timetables',
                        key: ClassActionTypes.SHOW_TIMETABLE_LIST_MODAL,
                    },
                    {
                        icon: <RiSubscript size={14} className="text-color-400"/>,
                        label: 'subjects',
                        key: ClassActionTypes.SHOW_SUBJECT_LIST_MODAL,
                    },
                    {
                        icon: <RiUserSharedLine size={14} className="text-green-500"/>,
                        label: 'students',
                        key: ClassActionTypes.SHOW_STUDENT_LIST_MODAL,
                    },
                    {
                        icon: <BiSolidEdit size={14} className="text-blue-500"/>,
                        label: 'edit',
                        key: ClassActionTypes.CLASS_UPDATE,
                    },
                    {
                        icon: <BiTrash size={14} className="text-primary-300"/>,
                        label: 'delete',
                        key: ClassActionTypes.CLASS_DELETE,
                    },
                ].filter(filter_option),
                (key) => {
                    handleAction(key, item)
                }
            ),
        [filter_option]
    )

    const modal = useMemo(() => {
        let title
        let Content = NullAsDefault
        let modal_params = {}
        let params = {title: undefined}
        const {id} = action?.value ?? {}
        let Wrapper = ({children}) => children({item: {}})
        switch (action.type) {
            case ClassActionTypes.SHOW_STUDENT_LIST_MODAL:
                return (
                    <FullSideModalLayout
                        right={true}
                        title={
                            <div className="font-bold text-color-000 tracking-wide 111-111">
                                {t('students of class')}
                            </div>
                        }
                        onCancel={onCancel}>
                        {bindingSearch({
                            onCancel,
                            owner_id,
                            owner_type,
                            item: action?.value,
                            container_type: AppEntities.COURSE_CLASS,
                        })(nest(
                            withProps(({item}) => ({item: getId(item)}))(CourseClassProvider),
                            CourseClassStudentList
                        ))}
                    </FullSideModalLayout>
                )
            case ClassActionTypes.SHOW_TIMETABLE_LIST_MODAL:
                return (
                    <FullSideModalLayout
                        right={true}
                        title={
                            <div className="font-bold text-color-000 tracking-wide">
                                {t('timetables')}
                            </div>
                        }
                        onCancel={onCancel}>
                        <CourseClassTimetables
                            item={action?.value}
                            {...{onCancel, owner_id, owner_type}}
                        />
                    </FullSideModalLayout>
                )
            case ClassActionTypes.SHOW_SUBJECT_LIST_MODAL:
                return (
                    <FullSideModalLayout
                        right={true}
                        title={
                            <div className="font-bold text-color-000 tracking-wide">
                                {t('subjects')}
                            </div>
                        }
                        onCancel={onCancel}>
                        <div className="p-3">
                            {bindingSearch({
                                onCancel,
                                item: action?.value,
                            })(CourseClassSubjectList)}
                        </div>
                    </FullSideModalLayout>
                )
            case ClassActionTypes.CLASS_CREATE:
                title = (
                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                        {t('create course class')}
                    </div>
                )
                params = {
                    onCancel,
                    onSubmit: (values) => {
                        const {course_id, ...rest} = values ?? {}
                        onCreate({...rest}, {id: course_id})
                    },
                }
                modal_params = {
                    width: 720,
                }
                Content = CreateCourseClassModal
                break
            case ClassActionTypes.CLASS_UPDATE:
                title = (
                    <div className="text-center font-bold text-color-000 uppercase tracking-wide">
                        {t('edit class')}
                    </div>
                )
                params = {
                    onCancel,
                    onSubmit: (values) => {
                        onUpdate(values, {id})
                    },
                }
                modal_params = {
                    width: 720,
                }
                Content = EditCourseClassModal
                Wrapper = withProps({id})(FetchClassById)
                break
            default:
                break
        }
        return (
            <Modal
                footer={null}
                title={title}
                onCancel={onCancel}
                open={action?.type}
                destroyOnHidden={true}
                className="custom-modal"
                {...modal_params}>
                <Wrapper>
                    {({item, isLoading}) => {
                        if (!!isLoading) {
                            return <LoadingBlock/>
                        }
                        return (
                            <Content
                                {...params}
                                {...{item, owner_id, owner_type}}
                            />
                        )
                    }}
                </Wrapper>
            </Modal>
        )
    }, [action, owner_id, owner_type])

    const refresh_deps = useMemo(
        () => [
            owner_id,
            owner_type,
            refreshKey,
            `refreshToken=${_.get(location.state, 'refreshToken')}`,
        ],
        [owner_id, owner_type, refreshKey, location.state]
    )

    const [pager_widget] = usePaginationHelper({
        filter_params: {
            owner_id,
            owner_type,
        },
        onInit: ({onSortChange = NullAsDefault, onFilterChange = NullAsDefault}) => {
            onFilterChange({
                course_id,
            })
            onSortChange(sortBy)
        },
        pagerInfo: {
            RenderEntity: React.Fragment,
            PaginationMeta: NullAsDefault,
            apiInfo: course_getCourseClassesByOwner_Api,
            query: bindQueryParams([{id: owner_id}, {type: owner_type}]),
            renderItem: renderCourseClass({
                translate: t,
                renderContextMenu,
            }),
            renderWrap: (child) => (
                <div className="flex flex-col divide-y divide-gray-200 list-item-workspace">
                    {child}
                </div>
            ),
            ...props,
        },
        SortByWidget: NullAsDefault,
        renderSortBy: NullAsDefault,
        isToggleDefault: false,
        query_params: filter_actions,
        AdvancedFilter: CourseClassFilter,
        dependencies: [owner_id, owner_type],
        withKey: createPagerKey(refresh_deps),
    })
    return (
        <div className="flex flex-1 flex-col space-y-3">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex flex-col flex-1">
                    <h2 className="font-bold text-xl text-gray-800 tracking-wide">
                        {t('classes')}
                    </h2>
                    <div className="my-1 w-20 h-1 rounded-full bg-gray-400"/>
                    <div className="mt-1 mr-4 text-sm text-color-300 tracking-wider 2222">
                        {t('note course class description')}
                    </div>
                </div>
                <Button
                    color="primary" variant="solid"
                    className="px-6 py-2 font-semibold rounded-lg"
                    onClick={() => {
                        handleAction(ClassActionTypes.CLASS_CREATE)
                    }}>
                    {t('create')}
                </Button>
            </div>
            {pager_widget}
            {modal}
            <div className="h-16"/>
        </div>
    )
}

const AdvancedHeader = withProps({
    description: 'note course class description',
})(ListHeader)

const CourseClasses = compose(
    branch(
        ({owner_id, owner_type}) => _.some([owner_id, owner_type], _.isEmpty),
        () => () => <ContentNotFound/>
    )
)(ClassesManager)

export const CourseClassesManager = compose(
    branch(
        ({course_id}) => !course_id,
        () => () => <ContentNotFound/>
    ),
    withProps(({course_id}) => ({
        apiInfo: course_getCourseClasses_Api,
        query: bindQueryParam({id: course_id}),
        // filter_option: ({ key }) =>
        //   [ClassActionTypes.CLASS_UPDATE, ClassActionTypes.CLASS_DELETE].includes(
        //     key
        //   ),
    }))
)(ClassesManager)

export default CourseClasses
